import { autoinject } from "aurelia-framework";

import { FetchClient } from "../common/fetch-client";
import {
    IGetUsersByRoleQuery,
    IGetUsersLean,
    IGetUsersLeanQuery,
    IGetUserSlimInfoResult,
    IUserResult,
    IGetUserByName,
    IGetAllUsersQuery,
    IGetAllUsersResponse,
    IGetScheduleEmployee,
    IGetScheduleEmployeeResponse,
    IGetUsersByAgency,
    IGetUsersLeanResult,
    IGetUsersLeanMessagingQuery,
    IGetClientRequest,
    IGetClientResponse,
    IGetUserRequest,
    IGetUserResponse,
    IGetUsersSlimRequest
} from "../interfaces/i-user";
import { User } from "../models/user";
import { IApplicationAndAccountQueryParam } from "../interfaces/i-get-referral";
import {
    INearbyEligibleEmployeesPanelList,
    INearbyEligibleEmployeesPinsList,
    INearbyEmpPanelList,
    INearbyEmpPanelListRes,
    INearbyEmpPinsList,
    INearbyEmpPinsListRes
} from "../resources-vue/vue-interfaces/i-visit-map";
import { IGetTaskComment } from "../resources-vue/vue-interfaces/i-visit-details";

@autoinject
export class UserService {
    private _user: IGetUserSlimInfoResult;
    private _fetchClient: FetchClient;
    private _baseUrl: string = "/api/v1/users";
    private _credentialsList: string[] = [];
    private _titlesList: string[] = [];

    public constructor(fetchClient: FetchClient) {
        this._fetchClient = fetchClient;
    }

    public async getUserCredentials() {
        if (!this._credentialsList.length) {
            this._credentialsList = await this._fetchClient.build(`${this._baseUrl}/credentials`).fetch<string[]>();
        }
        return this._credentialsList;
    }

    public async getCaseManager(payload: ICaseManagerSearchPayload) {
            return  await this._fetchClient
            .build(`${this._baseUrl}/case-managers`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<IGetCaseManagerResponse[]>();
    }

    public async getUserTitles() {
        if (!this._titlesList.length) {
            this._titlesList = await this._fetchClient.build(`${this._baseUrl}/titles`).fetch<string[]>();
        }
        return this._titlesList;
    }

    public async getUsersForCalendar(options: IEmployeeCalendarRequestParams) {
        return await this._fetchClient
            .build(`${this._baseUrl}/for-calendar`)
            .useMethod("POST")
            .useBody(options)
            .fetch<IPaginationNew<IEmployeeCalendarResponse>>();
    }

    public async getEmployeesForReport(options: IEmployeeScheduleCalendarRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/for-calendar-export`)
            .useMethod("GET")
            .useQueryString(options)
            .fetch<IPaginationNew<IEmployeeScheduleResponse>>();
    }

    public async calendarExport(options: ICalendarExportRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/calendar-export`)
            .useMethod("POST")
            .useBody(options)
            .fetchBlob();
    }

    public async getUsersByRole(query: IGetUsersByRoleQuery) {
        return await this._fetchClient.build(`${this._baseUrl}/role`).useQueryString(query).fetch<IUserResult[]>();
    }

    public async getCurrentUser() {
        return await this._fetchClient.build(`${this._baseUrl}/current`).fetch<IGetUserSlimInfoResult>();
    }

    public async getCurrentUserId(query: { agencyId: string }) {
        return await this._fetchClient
            .build(`${this._baseUrl}/current/id`)
            .useQueryString(query)
            .fetch<{ userId: string }>();
    }

    public async getUsersLean(query: IGetUsersLeanQuery) {
        return await this._fetchClient
            .build(`${this._baseUrl}/lean`)
            .useQueryString(query)
            .fetch<IPaginationCamelCaseNew<IGetUsersLean>>();
    }

    public async getUsersLeanMessaging(query: IGetUsersLeanMessagingQuery) {
        return await this._fetchClient
            .build(`${this._baseUrl}/lean`)
            .useQueryString(query)
            .fetch<IGetUsersLeanResult>();
    }

    public async getUser(userId: string) {
        return await this._fetchClient.build(`${this._baseUrl}/${userId}`).useMethod("GET").fetch<User>();
    }

    public async getUserByName(query: { term: string }) {
        return await this._fetchClient
            .build(`${this._baseUrl}/by-name`)
            .useQueryString(query)
            .fetch<IGetUserByName[]>();
    }

    public async getAllUsers(query: IGetAllUsersQuery) {
        return await this._fetchClient
            .build(`${this._baseUrl}/branch/all`)
            .useMethod("POST")
            .useBody(query)
            .fetch<IGetAllUsersResponse[]>();
    }

    public async getScheduleEmployee(query: IGetScheduleEmployee) {
        return await this._fetchClient
            .build(`${this._baseUrl}/schedule-employees`)
            .useQueryString(query)
            .fetch<IGetScheduleEmployeeResponse[]>();
    }

    public async getUsersByAgency(query: IApplicationAndAccountQueryParam) {
        return await this._fetchClient
            .build(`${this._baseUrl}/by-agency`)
            .useQueryString(query)
            .fetch<IGetUsersByAgency[]>();
    }

    public async getUserSlimInfo(userId: string) {
        return await this._fetchClient
            .build(`${this._baseUrl}/${userId}/slim`)
            .useMethod("GET")
            .fetch<IGetUserSlimInfoResult>();
    }

    public async getClients(query: IGetClientRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/staff-preference-clients`)
            .useQueryString(query)
            .fetch<IGetClientResponse[]>();
    }

    public async getUserByBranch(query: IGetUserRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/by-branch`)
            .useQueryString(query)
            .fetch<IGetUserResponse[]>();
    }

    public async getUserDetails() {
        return await this._fetchClient.build("/api/v1/beta/getUserDetails").fetch<IGetuserDetailResponse>();
    }

    public async updateUserBetaDetails(isBetaOpted: boolean) {
        return await this._fetchClient
            .build("/api/v1/beta/updateBetaToggle")
            .useMethod("PUT")
            .useBody({ isBetaOpted })
            .fetch<IGetuserDetailResponse>();
    }

    public async addUserBetaDetails(isBetaOpted: boolean) {
        return await this._fetchClient
            .build("/api/v1/beta/addUserDetails")
            .useMethod("POST")
            .useBody({ isBetaOpted })
            .fetchNoContent();
    }

    public async getActiveList(payload: IActiveListPayload) {
        return await this._fetchClient.build(`${this._baseUrl}/active`).useMethod("POST").useBody(payload).fetch();
    }

    public async getNearByEmpPinsList(payload: INearbyEmpPinsList) {
        return await this._fetchClient
            .build(`${this._baseUrl}/FindNearByEmployees`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<INearbyEmpPinsListRes[]>();
    }

    public async getNearByEligibleEmployeesPinsList(payload: INearbyEligibleEmployeesPinsList) {
        return await this._fetchClient
            .build(`${this._baseUrl}/FindNearByEligibleEmployees`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<INearbyEmpPinsListRes[]>();
    }

    public async getNearByEmpPanelList(payload: INearbyEmpPanelList) {
        return await this._fetchClient
            .build(`${this._baseUrl}/FindNearByEmployeesVisits`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<INearbyEmpPanelListRes>();
    }

    public async getNearByEligibleEmpPanelList(payload: INearbyEligibleEmployeesPanelList) {
        return await this._fetchClient
            .build(`${this._baseUrl}/FindNearByEligibleEmployeesVisits`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<INearbyEmpPanelListRes>();
    }

    public async getNearByClinicians(payload: INearbyEmpPanelList) {
        return await this._fetchClient
            .build(`${this._baseUrl}/FindNearByClinicians`)
            .useMethod("POST")
            .useBody(payload)
            .fetch<INearbyEmpPinsListRes[]>();
    }

    public async getUserById(query: IGetUsersSlimRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/slim/id`)
            .useQueryString(query)
            .fetch<IGetUserResponse>();
    }

    public async getUserDetailsById(query: IGetUsersSlimRequest) {
        return await this._fetchClient
            .build(`${this._baseUrl}/slim/id`)
            .useMethod("GET")
            .useQueryString(query)
            .fetch<IGetUserSlimInfoResult>();
    }
}

export interface IActiveListPayload {
    AccountId: string;
    LocationIds: string[];
    Term: string;
    Application: number;
}

export interface IEmployeeCalendarRequestParams {
    pageLength: number;
    page: number;
    term?: string;
    agencies?: string[];
    locationIds?: string[];
    credentials?: string[];
    scheduleFilter?: number;
    startDate?: Date | string;
    endDate?: Date | string;
    userIds?: [];
}

export interface IEmployeeScheduleCalendarRequest {
    pageLength: number;
    page: number;
    term?: string;
    agencies?: string[];
}

export interface ICalendarExportRequest {
    LocationIds: string[];
    AgencyIds: string[];
    StartDate: string;
    EndDate: string;
    UserIds: string[];
    StatusFilterList: any;
    ReportType: string;
    ReportView: string;
}

export interface IPaginationCamelCaseNew<T> {
    itemCount: number;
    pageLength: number;
    currentPage: number;
    pageCount: number;
    items: T[];
}

export interface IPaginationNew<T> {
    ItemCount: number;
    PageLength: number;
    CurrentPage: number;
    PageCount: number;
    Items: T[];
}

export interface IEmployeeCalendarResponse {
    Id: string;
    FirstName: string;
    LastName: string;
    PhoneNumber: string;
    Agencies: IAgency[];
    MiddleInitial: string;
}

export interface IEmployeeScheduleResponse {
    Id: string;
    EmployeeName: string;
    Agencies: IAgency[];
}

export interface IEmployeeFilterList {
    name: string;
    value: string;
    agencies?: IAgency[];
}

export interface IAgency {
    UserId: string;
    AgencyId: string;
    Application: number;
    AgencyName: string;
    Credentials?: string;
}

export interface IGetuserDetailResponse {
    Data: IGetuserDetails;
    IsSuccess: boolean;
    Message?: string;
}

export interface IGetuserDetails {
    Id: string;
    EmailAddress: string;
    IsBetaOpted: boolean;
}

export interface IGetuserDetailsRequest {
    emailAddress: string;
    isBetaOpted: boolean;
}

export interface IGetTaskResponseByUser {
    Comments: IGetTaskComment[];
}
export interface ICaseManagerId {
    Id: string;
    Application: number
}
export interface IGetCaseManagerResponse{
    LoginId: string;
    FirstName: string;
    LastName: string;
    CaseMagerIds: ICaseManagerId[];
}

export interface ICaseManagerOption {
    name: string;
    value: string;
    caseManagerIds: string[];
}
export interface ICaseManagerSearchPayload {
    Term: string;
    StartDate?: string;
    EndDate?: string;
    date?: string,
    LocationIds: string[];
    Agencies: string[];
    ScheduleFilter?: number;
    PatientStatus: number;
}